import { useEffect, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { useThemeStore } from "@/store/themeStore";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardOverviewComponent } from "@/components/dashboard/DashboardOverview";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useTranslation } from "react-i18next";
import { OnboardingTour } from "../components/OnboardingTour";
import { UpgradeDialog } from "@/components/subscription/UpgradeDialog";
import { useAuthStore } from "@/store/authStore";
import { useSubscriptionStore } from "@/store/subscriptionStore";
import { Button } from "@/components/ui/button";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { useToast } from "@/components/ui/use-toast";
import { useSidebarStore } from "@/store/sidebarStore";

const Dashboard = () => {
  const { theme } = useThemeStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { fetchStores, selectedStoreId, setSelectedStoreId } =
    useStoreManagementStore();
  const { user } = useAuthStore();
  const { showUpgradeDialog, checkSubscription } = useSubscriptionStore();
  const isSubscriptionEnabled = useFeatureFlagStore((state) =>
    state.checkFlag("enable_subscription")
  );
  const { toast } = useToast();
  const { collapsed } = useSidebarStore();

  const [isFirstTime, setIsFirstTime] = useState(() => {
    return !localStorage.getItem("onboardingCompleted");
  });

  // Handle payment success redirect
  useEffect(() => {
    const payment = searchParams.get("payment");
    const reference = searchParams.get("reference");
    const plan = searchParams.get("plan");

    if (payment === "success" && reference) {
      toast({
        title: "Payment Successful!",
        description:
          "Your subscription has been activated. Please wait a moment for the webhook to process.",
        duration: 5000,
      });

      // Refresh subscription status after a short delay to allow webhook processing
      setTimeout(() => {
        checkSubscription();
      }, 3000);

      // Clean up URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      url.searchParams.delete("reference");
      url.searchParams.delete("plan");
      window.history.replaceState({}, "", url.toString());
    }
  }, [searchParams, toast, checkSubscription]);

  useEffect(() => {
    // Apply theme to document
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }, [theme]);

  // Fetch stores on component mount
  useEffect(() => {
    if (user?.id) {
      fetchStores();
    }
  }, [fetchStores, user?.id]);

  // If we're at exactly /dashboard, render the DashboardOverview component
  const renderContent = () => {
    if (location.pathname === "/dashboard") {
      return <DashboardOverviewComponent storeId={selectedStoreId} />;
    }
    return <Outlet />;
  };

  // Only show dashboard overview title and store selector on the main dashboard page
  const isDashboardMainPage = location.pathname === "/dashboard";
  const { t } = useTranslation();
  return (
    <div className="h-screen overflow-hidden flex flex-col md:flex-row">
      <div
        className={`hidden md:block flex-shrink-0 ${
          collapsed ? "w-16" : "w-64"
        } transition-all duration-300`}
      >
        <DashboardSidebar />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardHeader />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {isDashboardMainPage && (
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {t("dashboardOverview.title")}
              </h1>
              {process.env.NODE_ENV === "development" &&
                isSubscriptionEnabled && (
                  <Button
                    onClick={() => {
                      useSubscriptionStore.setState({
                        showUpgradeDialog: true,
                        currentTrigger: {
                          type: "store_limit",
                          currentUsage: {
                            current: 1,
                            limit: 1,
                          },
                        },
                      });
                    }}
                  >
                    Test Upgrade Dialog
                  </Button>
                )}
            </div>
          )}
          {renderContent()}
        </main>
      </div>
      <OnboardingTour isFirstTime={isFirstTime} />
      {isSubscriptionEnabled && <UpgradeDialog />}
    </div>
  );
};

export default Dashboard;
